import { Icon, Typography } from '@mui/material'
import React from 'react'

import IconText from '@/app/component/layout/icon-text'
import { useCampaignContentProps } from '@/app/module/campaigns/component/hooks/use-campaign-content-props'
import CampaignContentWhatsappMultilingual from '@/app/module/campaigns/component/item/steps/content/whatsapp-multilingual'
import { WhatsAppMessageType } from '@/app/module/campaigns/component/item/steps/content/whatsapp-props'
import { CampaignTemplateType, ContentWhatsApp, WhatsAppMessage } from '@/app/module/campaigns/types'
import { PartFamily } from '@/app/module/campaigns/types/part'
import { WhatsAppTab } from '@/app/module/common/whatsapp/helpers'

type Props = {
  welcome: ContentWhatsApp | null
  onChange: (welcome: ContentWhatsApp | null) => void
}

const ChatbotWelcome: React.FC<Props> = ({ welcome, onChange }) => {
  const [playId, setPlayId] = React.useState<string>()
  const [tab, setTab] = React.useState<WhatsAppTab>(getDefaultTab(welcome))

  const item = React.useMemo<WhatsAppMessage>(() => toWelcomeItem(welcome), [welcome])

  const changeHandler = React.useCallback(
    (updatedItem: WhatsAppMessageType) => {
      const nextContent = updatedItem.message
      onChange(isWhatsAppContentEmpty(nextContent) ? null : nextContent)
    },
    [onChange],
  )

  const contentProps = useCampaignContentProps('chatbot-welcome')

  return (
    <>
      <IconText>
        <Icon color="info">waving_hand</Icon>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
          Welcome message for the chatbot. This message will be sent to users when they first interact with the chatbot.
        </Typography>
      </IconText>

      <CampaignContentWhatsappMultilingual
        {...contentProps}
        filterType={CampaignTemplateType.Chatbot}
        hideTemplateCheckbox
        info=""
        item={item}
        label="Message"
        playId={playId}
        required={false}
        tab={tab}
        changeHandler={changeHandler}
        onTabChange={setTab}
        setPlaying={setPlayId}
      />
    </>
  )
}

const waEmptyWelcome: ContentWhatsApp = {
  audio: '',
  document: '',
  image: '',
  text: '',
  video: '',
}

export const getDefaultTab = (content: ContentWhatsApp | null): WhatsAppTab => {
  if (content?.image) {
    return WhatsAppTab.Image
  }
  if (content?.video) {
    return WhatsAppTab.Video
  }
  if (content?.document) {
    return WhatsAppTab.Document
  }
  if (content?.audio) {
    return WhatsAppTab.Audio
  }
  return WhatsAppTab.Text
}

export const toWelcomeItem = (content: ContentWhatsApp | null): WhatsAppMessage =>
  ({
    id: 'chatbot-welcome',
    changed: false,
    type: PartFamily.WhatsApp,
    hasQuestion: false,
    message: content || waEmptyWelcome,
  }) as WhatsAppMessage

export const isWhatsAppContentEmpty = (content: ContentWhatsApp | null | undefined): boolean => {
  if (!content) {
    return true
  }

  const { text, audio, document, image, video } = content
  const hasText = Boolean(text && text.trim())
  const hasMedia = Boolean(audio || document || image || video)

  return !hasText && !hasMedia
}

export default ChatbotWelcome
